import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

export default function ObsidianAdminPanel({ token, isSuperAdmin }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [lastFlush, setLastFlush] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/obsidian/status`, { headers });
      setStatus(data);
    } catch { toast.error("خطأ في تحميل حالة Obsidian"); }
    setLoading(false);
  };

  const handleFlush = async () => {
    if (!isSuperAdmin) { toast.error("هذه العملية للمدير الرئيسي فقط"); return; }
    setFlushing(true);
    try {
      const { data } = await axios.post(`${API}/admin/obsidian/flush`, {}, { headers });
      setLastFlush(data);
      toast.success(`✓ مزامنة: ${data.success} نجح، ${data.failed} فشل`);
      loadStatus();
    } catch { toast.error("فشلت المزامنة"); }
    setFlushing(false);
  };

  const getConnectionStatus = () => {
    if (!status) return { label: "غير متصل", color: "#6b7280", icon: "⚫" };
    if (!status.api_key_set) return { label: "مفتاح API غير مضبوط", color: "#ef4444", icon: "🔴" };
    if (status.pending === 0 && status.done > 0) return { label: "متزامن", color: "#10b981", icon: "🟢" };
    if (status.pending > 0) return { label: `${status.pending} عملية معلّقة`, color: "#f59e0b", icon: "🟡" };
    return { label: "جاهز", color: "#10b981", icon: "🟢" };
  };

  const conn = getConnectionStatus();

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black">☁️ Obsidian Sync</h2>
          <p className="text-primary/50 text-sm mt-1">مزامنة قاعدة المحتوى مع Obsidian Vault</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadStatus} disabled={loading}
            className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold hover:bg-primary/20 transition-all">
            {loading ? "⏳" : "↻"} تحديث
          </button>
          {isSuperAdmin && (
            <button onClick={handleFlush} disabled={flushing || !status?.api_key_set}
              className="bg-primary text-secondary px-5 py-2 rounded-full text-sm font-bold hover:scale-105 transition-all disabled:opacity-40">
              {flushing ? "⏳ جارٍ المزامنة..." : "🔄 مزامنة الآن"}
            </button>
          )}
        </div>
      </div>

      {/* Connection status card */}
      <div className="bg-white border border-primary/10 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="text-4xl">{conn.icon}</div>
          <div>
            <div className="font-black text-xl" style={{ color: conn.color }}>{conn.label}</div>
            <div className="text-primary/40 text-sm mt-1">
              {status?.api_url ? `URL: ${status.api_url}` : "URL: غير مضبوط"}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className={`w-3 h-3 rounded-full ${status?.api_key_set ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-primary/60 font-bold">مفتاح API: {status?.api_key_set ? "مضبوط ✓" : "غير مضبوط ✗"}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className={`w-3 h-3 rounded-full ${status?.api_url ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-primary/60 font-bold">URL: {status?.api_url ? "مضبوط ✓" : "غير مضبوط ✗"}</span>
          </div>
        </div>
      </div>

      {/* Queue stats */}
      {status && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: "معلّق", value: status.pending ?? 0, color: "#f59e0b", bg: "#fef3c7" },
            { label: "ناجح", value: status.done ?? 0, color: "#10b981", bg: "#d1fae5" },
            { label: "فاشل", value: status.failed ?? 0, color: "#ef4444", bg: "#fee2e2" },
            { label: "إجمالي", value: (status.pending ?? 0) + (status.done ?? 0) + (status.failed ?? 0), color: "#6366f1", bg: "#ede9fe" },
          ].map((s, i) => (
            <div key={i} className="rounded-xl p-4 text-center" style={{ background: s.bg }}>
              <div className="text-2xl font-black" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs font-bold mt-1" style={{ color: s.color }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Last flush result */}
      {lastFlush && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <div className="font-bold text-green-700 mb-2">نتيجة آخر مزامنة</div>
          <div className="grid grid-cols-4 gap-3 text-sm">
            {[
              { label: "إجمالي", value: lastFlush.total },
              { label: "نجح", value: lastFlush.success },
              { label: "فشل", value: lastFlush.failed },
              { label: "تجاهل", value: lastFlush.skipped },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-black text-lg text-green-700">{s.value ?? 0}</div>
                <div className="text-green-600/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
        <div className="font-bold text-primary/70 text-sm mb-2">كيف يعمل النظام؟</div>
        <div className="space-y-1.5 text-xs text-primary/50">
          <div>• كل عملية (إضافة/تعديل/حذف) تُضاف تلقائياً لقائمة المزامنة.</div>
          <div>• "مزامنة الآن" ترسل العمليات المعلّقة لـ Obsidian عبر REST API.</div>
          <div>• يتطلب تشغيل Obsidian مع إضافة Local REST API على جهازك المحلي.</div>
          <div>• URL المضبوط حالياً: <code className="bg-primary/10 px-1 rounded">{status?.api_url || "غير مضبوط"}</code></div>
        </div>
      </div>
    </div>
  );
}
